import { Delete, Edit, Send } from "@mui/icons-material";
import {
  Box,
  FormGroup,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography
} from "@mui/material";
import { useState } from "react";
import { TNotify } from "../types/AllTypes";
import { MyButton } from "./MyButton";

export const Notify = ({
  notify,
  onEdit,
  onEditSend,
  onDelete
}: {
  notify: TNotify;
  onEdit?: () => void;
  onEditSend?: () => void;
  onDelete?: () => void;
}) => {
  return (
    <Box padding={3} paddingY={1.5} width={"350px"} sx={{ margin: "0 auto" }}>
      <Paper elevation={1}>
        <Box padding={2}>
          <Grid container>
            <Grid item xs={12}>
              <Typography variant="h5" color={"primary.main"}>
                {notify.title}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color={"text.primary"}>
                {notify.body}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Box display={"flex"} justifyContent={"end"}>
                <Typography>
                  {onEdit && (
                    <IconButton onClick={onEdit} color="primary">
                      <Edit />
                    </IconButton>
                  )}
                  {onEditSend && (
                    <IconButton onClick={onEditSend} color="primary">
                      <Send />
                    </IconButton>
                  )}
                  {onDelete && (
                    <IconButton onClick={onDelete} color="primary">
                      <Delete />
                    </IconButton>
                  )}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export const NotifyForm = ({
  notify,
  onChange,
  onSendAndSave = undefined,
  onSend = undefined,
  onSave = undefined,
  inDrawer = false
}: {
  notify: TNotify;
  onChange: <T extends keyof TNotify>(
    fieldChanged: T,
    newValue: TNotify[T]
  ) => void;
  onSendAndSave?: () => void;
  onSend?: () => void;
  onSave?: () => void;
  inDrawer?: boolean;
}) => {
  const [errors, setErrors] = useState({
    title: "",
    body: ""
  });

  const handleSubmit = (onValid?: () => void) => {
    if (!notify.title.trim() || !notify.body.trim()) {
      setErrors({
        title: notify.title.trim() ? "" : "Zadaj nadpis",
        body: notify.body.trim() ? "" : "Zadaj text"
      });
    } else {
      onValid?.();
    }
  };

  return (
    <>
      <Box
        padding={3}
        marginBottom={5}
        paddingBottom={10}
        paddingTop={inDrawer ? 0 : 3}
      >
        <FormGroup>
          <TextField
            required
            sx={{ margin: 1 }}
            value={notify.title}
            onChange={e => {
              onChange("title", e.target.value);
              setErrors({
                ...errors,
                title: e.target.value.trim() ? "" : "Zadaj nadpis"
              });
            }}
            label="Title"
            variant="standard"
            error={errors.title.length > 0}
            helperText={errors.title}
            fullWidth
          />
          <TextField
            required
            sx={{ margin: 1 }}
            value={notify.body}
            onChange={e => {
              onChange("body", e.target.value);
              setErrors({
                ...errors,
                body: e.target.value.trim() ? "" : "Zadaj text"
              });
            }}
            label="Body"
            variant="standard"
            error={errors.body.length > 0}
            helperText={errors.body}
            fullWidth
          />
        </FormGroup>
      </Box>
      <Box
        position={"fixed"}
        bottom={inDrawer ? 20 : 60}
        width={inDrawer ? "100%" : "92vw"}
        paddingX={2}
      >
        {onSendAndSave && (
          <MyButton
            onClick={() => handleSubmit(onSendAndSave)}
            sx={{ width: "48%", marginRight: "4%" }}
          >
            <Typography
              sx={{
                fontWeight: 700
              }}
              variant="h6"
            >
              Ulozit a poslat
            </Typography>
          </MyButton>
        )}
        {onSend && (
          <MyButton
            sx={{ width: onSendAndSave ? "48%" : "100%" }}
            onClick={() => handleSubmit(onSend)}
          >
            <Typography
              sx={{
                fontWeight: 700
              }}
              variant="h6"
            >
              Poslat
            </Typography>
          </MyButton>
        )}
        {onSave && (
          <MyButton sx={{ width: "100%" }} onClick={() => handleSubmit(onSave)}>
            <Typography
              sx={{
                fontWeight: 700
              }}
              variant="h6"
            >
              Ulozit
            </Typography>
          </MyButton>
        )}
      </Box>
    </>
  );
};

export const AllowNotify = ({
  text,
  onClick
}: {
  text: string;
  onClick: () => void;
}) => {
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      flexDirection="row"
      padding={2}
      flexWrap="wrap"
      border={1}
      borderColor="primary.main"
      borderRadius={2}
      sx={{
        backgroundColor: "primary.dark",
        width: "100%",
        maxWidth: 600,
        margin: "0 auto",
        marginBottom: 2,
        boxShadow: 3
      }}
    >
      <Typography variant="h6" sx={{ textAlign: "center", marginRight: 2 }}>
        {text}
      </Typography>
      <MyButton onClick={onClick} variant="contained" sx={{ marginTop: 2 }}>
        Povolit posielanie notifikacii
      </MyButton>
    </Box>
  );
};

export const NotifyTemplates = ({
  notifyTemplates,
  onSend,
  onEdit,
  onDelete
}: {
  notifyTemplates: TNotify[];
  onSend?: (notify: TNotify) => void;
  onEdit?: (notify: TNotify) => void;
  onDelete?: (notify: TNotify) => void;
}) => {
  return (
    <Box>
      <Grid
        container
        justifyContent={"center"}
        maxWidth={"1200px"}
        margin="0 auto"
      >
        {notifyTemplates.length === 0 && (
          <Box display={"flex"} justifyContent={"center"}>
            <Typography variant="h4" sx={{ textAlign: "center", marginTop: 3 }}>
              Zatial ziadne ulozene notifikacie
            </Typography>
          </Box>
        )}
        {notifyTemplates.map((template, idx) => {
          return (
            <Notify
              key={`${template.id ?? template.title}-${idx}`}
              notify={template}
              onEditSend={
                onSend
                  ? () => {
                      onSend(template);
                    }
                  : undefined
              }
              onEdit={
                onEdit
                  ? () => {
                      onEdit(template);
                    }
                  : undefined
              }
              onDelete={
                onDelete
                  ? () => {
                      onDelete(template);
                    }
                  : undefined
              }
            />
          );
        })}
      </Grid>
    </Box>
  );
};
